/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * Aui 의 컴포넌트 및 모듈의 기본 클래스를 정의한다.
 *
 * @file /src/base/Base.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 9. 18.
 */
import { Exceptions } from '@utils/Exceptions';

export interface Listeners<T> {
    /**
     * Aui 요소가 초기화되었을 때
     */
    init?: (me: T) => void | Promise<void>;

    /**
     * Aui 요소가 제거되었을 때
     */
    destroy?: () => void | Promise<void>;

    /**
     * 이벤트리스너 확장
     */
    [event: string]: ((...args: any[]) => any | Promise<any>) | undefined;
}

export interface Properties<T> {
    /**
     * 참조하고 있는 객체가 더이상 존재하지 않을 경우, 마지막 release() 시 객체를 자동으로 제거할 지 여부
     */
    autoDestroy?: boolean;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export abstract class Base {
    /**
     * 참조하고 있는 객체가 더이상 존재하지 않을 경우, 마지막 release() 시 객체를 자동으로 제거할 지 여부
     */
    autoDestroy: boolean;

    /**
     * 객체를 참조하고 있는 다른 객체의 갯수
     */
    referenceCount: number = 0;

    /**
     * 객체가 제거되었는지 여부
     */
    isDestroyed: boolean = false;

    /**
     * 이벤트리스너
     */
    listeners: { [key: string]: Function[] };

    /**
     * 현재 실행중인(비동기 처리가 끝나지 않은) 이벤트 목록
     */
    private pendingEvents: Set<Promise<void>> = new Set();

    /**
     * 기본클래스를 정의한다.
     *
     * @param properties - Aui 구성요소의 초기설정
     */
    constructor(public properties: Properties<Base> = null) {
        this.listeners = {};

        this.autoDestroy = properties?.autoDestroy !== false;

        if (properties?.listeners !== undefined) {
            const listeners = properties.listeners as Record<string, (...args: any[]) => any>;
            for (const event in listeners) {
                const handler = listeners[event];
                if (handler !== undefined) {
                    if (!this.listeners[event]) {
                        this.listeners[event] = [];
                    }
                    this.listeners[event].push(handler);
                }
            }
        }

        this.onInit();
    }

    /**
     * 이벤트를 등록한다.
     *
     * @param event - 이벤트명
     * @param handler - 이벤트핸들러
     */
    addEvent<E extends keyof this['properties']['listeners']>(
        event: E,
        handler: NonNullable<this['properties']['listeners'][E]>
    ): void {
        this.listeners[event as string] ??= [];
        this.listeners[event as string].push(handler as Function);
    }

    /**
     * 등록된 이벤트를 제거한다.
     *
     * @param event - 제거할 이벤트명
     * @param handler - 제거할 이벤트핸들러 (정의되지 않은 경우 해당 이벤트 전체를 제거한다.)
     */
    removeEvent(event: string, handler?: Function): void {
        if (handler) {
            this.listeners[event] = (this.listeners[event] ?? []).filter((h) => h !== handler);
        } else {
            delete this.listeners[event];
        }
    }

    /**
     * 이벤트가 존재하는지 확인한다.
     *
     * @param event - 이벤트명
     * @returns 이벤트존재여부
     */
    hasEvent(event: string): boolean {
        return ((this.listeners ?? {})[event] ?? []).length > 0;
    }

    /**
     * 이벤트를 발생시킨다.
     *
     * @param event - 이벤트명
     * @param args - 이벤트리스너에 전달할 인자
     */
    async fireEvent<E extends keyof this['properties']['listeners']>(event: E, args: any[] = []): Promise<void> {
        const listeners = this.listeners?.[event as string] ?? [];
        if (listeners.length === 0) return;

        const promise = Promise.all(listeners.map((fn) => Promise.resolve(fn(...args)))).then(() => {});
        this.pendingEvents.add(promise);

        try {
            await promise;
        } finally {
            this.pendingEvents.delete(promise);
        }
    }

    /**
     * 현재 실행중인 이벤트리스너가 모두 종료될때까지 대기한다.
     *
     * 이벤트리스너 내부에서 비동기로 다른 컴포넌트를 참조하는 도중,
     * 해당 컴포넌트(또는 자기 자신)가 제거되어 참조가 유실되는 것을 방지하기 위해 사용한다.
     */
    async waitForEvents(): Promise<void> {
        if (this.pendingEvents.size === 0) return;
        await Promise.all(this.pendingEvents);
    }

    /**
     * 값을 재귀적으로 복제한다.
     * Base 인스턴스는 clone(), 배열은 요소별 재귀, plain object는 키별 재귀, 그 외는 참조 복사.
     *
     * @param value - 복제할 값
     * @returns 복제된 값
     */
    private static cloneValue(value: any): any {
        if (value === null || value === undefined) {
            return value;
        }

        if (value instanceof Base) {
            return value.clone();
        }

        if (Array.isArray(value) == true) {
            return value.map((item) => Base.cloneValue(item));
        }

        if (typeof value === 'object' && value.constructor === Object) {
            const result: Record<string, any> = {};
            for (const key of Object.keys(value)) {
                result[key] = Base.cloneValue(value[key]);
            }
            return result;
        }

        if (typeof value === 'object') {
            Exceptions.warn('CLONE_NOT_SUPPORTED', value.constructor?.name ?? 'Unknown', value);
        }
        return value;
    }

    /**
     * 객체를 복제한다.
     * properties 를 재귀적으로 복제하여 동일한 타입의 새 인스턴스를 반환한다.
     * id 키는 복제에서 제외하며, properties 인자를 전달하면 해당 값으로 덮어쓴다.
     *
     * @param properties - 복제 시 덮어쓸 속성
     * @returns 복제된 인스턴스
     */
    clone(properties: { [key: string]: any } = {}): this {
        const cloned = Base.cloneValue(this.properties);
        delete cloned.id;
        return new (this.constructor as any)({ ...cloned, ...properties });
    }

    /**
     * 객체를 참조하고 있다고 선언한다.
     */
    retain(): void {
        this.referenceCount++;
    }

    /**
     * 객체를 더이상 참조하지 않는다고 선언한다.
     */
    release(): void {
        if (this.referenceCount > 0) {
            this.referenceCount--;

            if (this.referenceCount == 0 && this.autoDestroy == true) {
                this.destroy();
            }
        } else {
            Exceptions.warn('ALREADY_RELEASED');
        }
    }

    /**
     * 객체가 제거가능한지 확인한다.
     */
    isDestroyable(): boolean {
        return this.referenceCount == 0;
    }

    /**
     * 객체를 제거한다.
     */
    destroy(): void {
        if (this.isDestroyable() == true) {
            this.isDestroyed = true;
            this.listeners = null;
            this.properties = null;
            this.onDestroy();
        }
    }

    /**
     * 객체가 초기화되었을 때 이벤트를 처리한다.
     */
    onInit(): void {
        this.fireEvent('init', [this]);
    }

    /**
     * 객체가 제거되었을 때 이벤트를 처리한다.
     */
    onDestroy(): void {
        this.fireEvent('destroy');
    }
}
